import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Logo } from "./Logo";
import { TID } from "../constants/testIds";
import { Globe, ArrowUpRight } from "lucide-react";

export function Header({ lang, setLang, t, onCtaClick }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about, tid: TID.navAbout },
    { id: "services", label: t.nav.services, tid: TID.navServices },
    { id: "how", label: t.nav.how, tid: "nav-how" },
    { id: "calculator", label: t.nav.calculator, tid: "nav-calculator" },
    { id: "faq", label: t.nav.faq, tid: "nav-faq" },
    { id: "contact", label: t.nav.contact, tid: TID.navContact },
  ];

  const handleLink = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0B132B]/85 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        <a
          href="#top"
          onClick={(e) => handleLink(e, "top")}
          data-testid={TID.logoLink}
          className="flex items-center"
        >
          <Logo />
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={l.tid}
              onClick={(e) => handleLink(e, l.id)}
              className="text-[13px] uppercase tracking-[0.18em] text-[#9BA1A6] hover:text-white transition-colors duration-200"
              style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 500 }}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Language toggle */}
          <div
            data-testid={TID.langToggle}
            className="hidden sm:flex items-center gap-1 border border-white/15 rounded-sm p-[2px]"
            role="group"
            aria-label="Language toggle"
          >
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2 py-1 text-[11px] tracking-widest uppercase rounded-sm transition-all ${
                lang === "en"
                  ? "bg-[#00E5FF] text-[#0B132B] font-bold"
                  : "text-[#9BA1A6] hover:text-white"
              }`}
            >
              EN
            </button>
            <button
              data-testid="lang-ro"
              onClick={() => setLang("ro")}
              className={`px-2 py-1 text-[11px] tracking-widest uppercase rounded-sm transition-all ${
                lang === "ro"
                  ? "bg-[#00E5FF] text-[#0B132B] font-bold"
                  : "text-[#9BA1A6] hover:text-white"
              }`}
            >
              RO
            </button>
          </div>
          {/* Mobile language icon */}
          <button
            data-testid="lang-toggle-mobile"
            onClick={() => setLang(lang === "en" ? "ro" : "en")}
            className="sm:hidden flex items-center gap-1 text-white border border-white/15 px-2 py-1 rounded-sm text-[11px] uppercase tracking-widest"
            aria-label="Toggle language"
          >
            <Globe size={12} />
            {lang.toUpperCase()}
          </button>

          <button
            data-testid={TID.navCta}
            onClick={onCtaClick}
            className="hidden md:inline-flex items-center gap-2 bg-[#00E5FF] hover:bg-[#00C2D6] text-[#0B132B] font-bold text-[12px] uppercase tracking-[0.18em] px-5 py-3 rounded-sm transition-all duration-300"
            style={{ boxShadow: "0 0 0 1px rgba(0,229,255,0.0)" }}
          >
            {t.nav.cta}
            <ArrowUpRight size={14} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </motion.header>
  );
}
